import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import { CancelButton, CreateStoreButton, CustomDialogActions } from "./style";

interface CreateStoreDialogProps {
  open: boolean;
  onClose: () => void;
  propertyData: Property;
  onSubmit: (storeData: {
    id: string;
    store_name: string;
    short_name: string;
    property_id: string;
  }) => void;
}

const CreateStoreDialog: React.FC<CreateStoreDialogProps> = ({
  open,
  onClose,
  propertyData,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    id: "",
    store_name: "",
    short_name: "",
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }
    };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.id.trim()) {
      newErrors.id = "Store ID is required";
    }

    if (!formData.store_name.trim()) {
      newErrors.store_name = "Store name is required";
    }

    if (!formData.short_name.trim()) {
      newErrors.short_name = "Short name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit({
        ...formData,
        property_id: propertyData.id,
      });
    }
  };

  const handleClose = () => {
    setFormData({
      id: "",
      store_name: "",
      short_name: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Store</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Property:{" "}
            <strong>
              {propertyData.property_name} ({propertyData.short_name})
            </strong>
          </Typography>
        </Box>

        <TextField
          fullWidth
          label="Store ID"
          value={formData.id}
          onChange={handleInputChange("id")}
          error={!!errors.id}
          helperText={errors.id}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="Store Name"
          value={formData.store_name}
          onChange={handleInputChange("store_name")}
          error={!!errors.store_name}
          helperText={errors.store_name}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />

        <TextField
          fullWidth
          label="Short Name"
          value={formData.short_name}
          onChange={handleInputChange("short_name")}
          error={!!errors.short_name}
          helperText={errors.short_name}
          inputProps={{ maxLength: 100 }}
          margin="normal"
          required
        />
      </DialogContent>
      <CustomDialogActions>
        <CancelButton onClick={handleClose}>Cancel</CancelButton>
        <CreateStoreButton
          onClick={handleSubmit}
          variant="contained"
          color="primary"
        >
          Create Store
        </CreateStoreButton>
      </CustomDialogActions>
    </Dialog>
  );
};

export default CreateStoreDialog;
